import { useQuery, useQueryClient, type QueryClient } from '@tanstack/vue-query';
import * as Sentry from '@sentry/vue';
import { computed, toValue, type MaybeRefOrGetter } from 'vue';
import http from '@/services/http';
import { entitySlugFor } from '@/composables/entityColumns';
import { liveRecords } from '@/composables/liveRecords/liveRecords';

/**
 * One record's full search document, fetched together with every other record
 * of the same entity asked for in the same render.
 *
 * {@link useModelRecordsByIds} needs every id up front. A list of cards cannot
 * give it that, because each card only knows its own record. So each card calls
 * this, and the ids read in one render are collected and sent as one search:
 *
 *   POST /api/<slug>/search  { filter: id in [...] }
 *
 * The schedule search page uses it: each schedule's card draws its custom list,
 * and a page of fifty schedules becomes one search for their lists.
 *
 * A record the search does not return comes back as null. It may not be indexed
 * yet, or this user may not see it. The caller then draws what it already has.
 * Each document found is also written under {@link useModelRecordQuery}'s key,
 * so opening that record in the inspector does not ask again. A record that was
 * not found is not written there, so the inspector still tries its own fallback.
 */

/** Matches the batch size useModelRecordsByIds and useZoneSummaries use for the same search. */
const BATCH_SIZE = 200;

type Pending = { resolve: (record: Record<string, unknown> | null) => void; reject: (error: unknown) => void };

// Waiting requests, per entity and then per id. Several cards can wait on the same id.
const queues = new Map<string, Map<string, Pending[]>>();
let scheduled = false;

function asIdValue(id: string): number | string {
    return /^\d+$/.test(id) ? Number(id) : id;
}

async function fetchBatch(entityId: string, ids: string[], waiting: Map<string, Pending[]>, queryClient: QueryClient): Promise<void> {
    try {
        const response = await http.post(`/api/${entitySlugFor(entityId)}/search`, {
            perPage: ids.length,
            withTotal: false,
            filter: {
                ruleScopes: {
                    id: 'root',
                    scopeType: 'and',
                    rules: [{ id: 'records', column: 'id', criterion: 'in', value: ids.map(asIdValue), field: null }],
                },
            },
        });
        const byId = new Map<string, Record<string, unknown>>();
        for (const record of (response.data as { data?: Record<string, unknown>[] }).data ?? []) {
            byId.set(String(record.id), record);
            queryClient.setQueryData(['model-record', entityId, String(record.id)], record);
        }
        for (const id of ids) {
            for (const pending of waiting.get(id) ?? []) pending.resolve(byId.get(id) ?? null);
        }
    } catch (error) {
        // The cards still draw what they already have, so report the failure rather than stay quiet.
        Sentry.captureException(error);
        for (const id of ids) {
            for (const pending of waiting.get(id) ?? []) pending.reject(error);
        }
    }
}

function flush(queryClient: QueryClient): void {
    scheduled = false;
    const current = new Map(queues);
    queues.clear();
    for (const [entityId, waiting] of current) {
        const ids = [...waiting.keys()];
        for (let index = 0; index < ids.length; index += BATCH_SIZE) {
            void fetchBatch(entityId, ids.slice(index, index + BATCH_SIZE), waiting, queryClient);
        }
    }
}

function load(entityId: string, recordId: string, queryClient: QueryClient): Promise<Record<string, unknown> | null> {
    return new Promise((resolve, reject) => {
        const waiting = queues.get(entityId) ?? new Map<string, Pending[]>();
        queues.set(entityId, waiting);
        waiting.set(recordId, [...(waiting.get(recordId) ?? []), { resolve, reject }]);
        if (!scheduled) {
            scheduled = true;
            // A microtask, so every id asked for in this render joins the same request.
            void Promise.resolve().then(() => flush(queryClient));
        }
    });
}

/** The cache key one record's batched document lives under. */
export function batchedModelRecordQueryKey(entityId: string, recordId: number | string) {
    return ['model-record-batched', entityId, String(recordId)] as const;
}

export function useBatchedModelRecord(
    entityId: MaybeRefOrGetter<string>,
    recordId: MaybeRefOrGetter<number | string | null | undefined>,
) {
    const queryClient = useQueryClient();
    return useQuery({
        queryKey: computed(() => batchedModelRecordQueryKey(toValue(entityId), String(toValue(recordId)))),
        queryFn: () => load(toValue(entityId), String(toValue(recordId)), queryClient),
        enabled: computed(() => Boolean(entitySlugFor(toValue(entityId))) && Boolean(toValue(recordId))),
        meta: computed(() => liveRecords(toValue(entityId), [toValue(recordId)])),
        retry: 0,
    });
}
